/**
 * SideEdgeGhostModel — renders transparent ghost shapes for side edge features.
 *
 * Rendering: only the feature(s) an edit actually touches get a ghost — never the
 * whole set (a tablet can carry dozens of speaker grills). Three additive sources:
 * - preview store (`previewSideEdge`): the feature being dragged live on a slider.
 * - pending keys (from DeviceMeshV2, diff of added/changed features): the feature(s)
 *   touched by a slider commit, undo/redo, or creation — shown during the worker
 *   rebuild, cleared when it finishes. This is what keeps a ghost on undo/redo.
 * - removed store (`removedSideEdgeStore`): red ghosts for deleted features.
 *
 * Each feature type maps to a simple ghost shape:
 * - sideButton / sideSpeakerGrill / chargingConnector / simCardSlot → rounded rect
 * - antenna → thin rect
 * - screw → circle
 */
import {useMemo, useEffect, useRef, useSyncExternalStore} from 'react'
import * as THREE from 'three'
import {createRoundedRectShape} from '../../../utils/geometry'
import {useAppSelector} from '../../../store'
import {selectActiveScene} from '../../store/scenesSlice'
import {getPreviewSideEdgeSnapshot, subscribePreviewSideEdge, clearPreviewSideEdge} from '../previewSideEdge'
import {getRemovedSideEdgeSnapshot, subscribeRemovedSideEdge, clearRemovedSideEdge} from '../removedSideEdgeStore'
import type {SideEdge, SideEdgeFeatureConfig} from '../../types'
import {GHOST_COLOR, REMOVED_COLOR, GHOST_OPACITY} from './ghostStyle'

const Z_OFFSET = 0.15
/** Visual marker size for the SIM eject pin-hole (actual hole ⌀ is tiny — enlarge so the drag is visible) */
const PIN_HOLE_GHOST_DIAMETER = 0.8
/** Nudge the pin-hole marker outward from the slot rect so it reads on top */
const PIN_HOLE_GHOST_Z = 0.05

// ── Helper — compute edge position and rotation ──────────────────

interface EdgeTransform {
  /** Position [x, y, z] of the shape center */
  pos: [number, number, number]
  /** Euler rotation to face outward from edge */
  rotation: [number, number, number]
  /** Whether width/height need swapping (top/bottom edges) */
  swapAxes: boolean
}

const getEdgeTransform = (
  edge: SideEdge,
  position: number,
  depth: number,
  deviceWidth: number,
  deviceHeight: number,
): EdgeTransform => {
  const halfW = deviceWidth / 2
  const halfH = deviceHeight / 2

  switch (edge) {
    case 'right':
      return {pos: [halfW + depth + Z_OFFSET, position, 0], rotation: [0, Math.PI / 2, 0], swapAxes: false}
    case 'left':
      return {pos: [-(halfW + depth + Z_OFFSET), position, 0], rotation: [0, -Math.PI / 2, 0], swapAxes: false}
    case 'top':
      return {pos: [position, halfH + depth + Z_OFFSET, 0], rotation: [-Math.PI / 2, 0, 0], swapAxes: true}
    case 'bottom':
      return {pos: [position, -(halfH + depth + Z_OFFSET), 0], rotation: [Math.PI / 2, 0, 0], swapAxes: true}
  }
}

/**
 * Shift a transform along the edge axis by `offset` (mm) for the SIM pin-hole marker.
 * In the slot's local frame the edge axis is local-X for top/bottom (swapAxes) and
 * local-Y for left/right; a small +Z nudge lifts the marker above the slot rect.
 */
const shiftAlongEdge = (transform: EdgeTransform, offset: number): EdgeTransform => {
  const localOffset = transform.swapAxes
    ? new THREE.Vector3(offset, 0, PIN_HOLE_GHOST_Z)
    : new THREE.Vector3(0, offset, PIN_HOLE_GHOST_Z)
  localOffset.applyEuler(new THREE.Euler(transform.rotation[0], transform.rotation[1], transform.rotation[2]))
  return {
    pos: [transform.pos[0] + localOffset.x, transform.pos[1] + localOffset.y, transform.pos[2] + localOffset.z],
    rotation: transform.rotation,
    swapAxes: transform.swapAxes,
  }
}

// ── Single ghost shape — rounded rect ────────────────────────────

interface RectGhostProps {
  width: number
  height: number
  cornerRadius: number
  transform: EdgeTransform
  color?: number
  disableDepthTest?: boolean
}

const RectGhost = ({width, height, cornerRadius, transform, color = GHOST_COLOR, disableDepthTest}: RectGhostProps) => {
  const cr = Math.min(cornerRadius, width / 2, height / 2)

  const prevGeoRef = useRef<THREE.ShapeGeometry | null>(null)
  const geo = useMemo(() => {
    const shape = createRoundedRectShape(width, height, cr)
    return new THREE.ShapeGeometry(shape, 16)
  }, [width, height, cr])

  useEffect(() => {
    if (prevGeoRef.current && prevGeoRef.current !== geo) {
      prevGeoRef.current.dispose()
    }
    prevGeoRef.current = geo
    return () => {
      geo.dispose()
    }
  }, [geo])

  const euler = useMemo(
    () => new THREE.Euler(transform.rotation[0], transform.rotation[1], transform.rotation[2]),
    [transform.rotation],
  )

  return (
    <mesh geometry={geo} position={transform.pos} rotation={euler}>
      <meshBasicMaterial
        color={color}
        transparent
        opacity={GHOST_OPACITY}
        depthWrite={false}
        depthTest={!disableDepthTest}
        side={THREE.DoubleSide}
      />
    </mesh>
  )
}

// ── Single ghost shape — circle ──────────────────────────────────

interface CircleGhostProps {
  diameter: number
  transform: EdgeTransform
  color?: number
  disableDepthTest?: boolean
}

const CircleGhost = ({diameter, transform, color = GHOST_COLOR, disableDepthTest}: CircleGhostProps) => {
  const prevGeoRef = useRef<THREE.CircleGeometry | null>(null)
  const geo = useMemo(() => new THREE.CircleGeometry(diameter / 2, 32), [diameter])

  useEffect(() => {
    if (prevGeoRef.current && prevGeoRef.current !== geo) {
      prevGeoRef.current.dispose()
    }
    prevGeoRef.current = geo
    return () => {
      geo.dispose()
    }
  }, [geo])

  const euler = useMemo(
    () => new THREE.Euler(transform.rotation[0], transform.rotation[1], transform.rotation[2]),
    [transform.rotation],
  )

  return (
    <mesh geometry={geo} position={transform.pos} rotation={euler}>
      <meshBasicMaterial
        color={color}
        transparent
        opacity={GHOST_OPACITY}
        depthWrite={false}
        depthTest={!disableDepthTest}
        side={THREE.DoubleSide}
      />
    </mesh>
  )
}

// ── Preview-based ghost (from preview store, not Redux) ──────────

interface PreviewGhostProps {
  previewKey: string
  preview: {
    type: SideEdgeFeatureConfig['type']
    edge: SideEdge
    position: number
    width: number
    height: number
    cornerRadius: number
    diameter: number
    depth: number
    chamfer: number
    pinHoleOffset?: number
  }
  deviceWidth: number
  deviceHeight: number
  deviceDepth: number
  color?: number
  disableDepthTest?: boolean
}

const PreviewGhost = ({
  preview,
  deviceWidth,
  deviceHeight,
  deviceDepth,
  color,
  disableDepthTest,
}: PreviewGhostProps) => {
  const transform = getEdgeTransform(preview.edge, preview.position, preview.depth, deviceWidth, deviceHeight)
  const swap = transform.swapAxes

  switch (preview.type) {
    case 'sideButton': {
      // For chamfered side buttons, the ghost shows the wider hole entrance (footprint at surface)
      const ghostW = preview.width + 2 * preview.chamfer
      const ghostH = preview.height + 2 * preview.chamfer
      const ghostCr = preview.cornerRadius + preview.chamfer
      const w = swap ? ghostH : ghostW
      const h = swap ? ghostW : ghostH
      return (
        <RectGhost
          width={w}
          height={h}
          cornerRadius={ghostCr}
          transform={transform}
          color={color}
          disableDepthTest={disableDepthTest}
        />
      )
    }
    case 'muteSwitch':
    case 'sideSpeakerGrill': {
      const w = swap ? preview.height : preview.width
      const h = swap ? preview.width : preview.height
      return (
        <RectGhost
          width={w}
          height={h}
          cornerRadius={preview.cornerRadius}
          transform={transform}
          color={color}
          disableDepthTest={disableDepthTest}
        />
      )
    }
    case 'simCardSlot': {
      // Slot rect + a circular marker for the eject pin-hole, offset along the tray
      // so dragging pinHoleOffset is visible (the CSG hole is too small to see here).
      const w = swap ? preview.height : preview.width
      const h = swap ? preview.width : preview.height
      const pinTransform = shiftAlongEdge(transform, preview.pinHoleOffset ?? 0)
      return (
        <>
          <RectGhost
            width={w}
            height={h}
            cornerRadius={preview.cornerRadius}
            transform={transform}
            color={color}
            disableDepthTest={disableDepthTest}
          />
          <CircleGhost
            diameter={PIN_HOLE_GHOST_DIAMETER}
            transform={pinTransform}
            color={color}
            disableDepthTest={disableDepthTest}
          />
        </>
      )
    }
    case 'chargingConnector': {
      // Charging connector width = along edge, height = opening on face (opposite of sideButton)
      const w = swap ? preview.width : preview.height
      const h = swap ? preview.height : preview.width
      return (
        <RectGhost
          width={w}
          height={h}
          cornerRadius={preview.cornerRadius}
          transform={transform}
          color={color}
          disableDepthTest={disableDepthTest}
        />
      )
    }
    case 'antenna': {
      const faceLen = deviceDepth
      const bandLen = preview.width
      return (
        <RectGhost
          width={swap ? bandLen : faceLen}
          height={swap ? faceLen : bandLen}
          cornerRadius={0}
          transform={transform}
          color={color}
          disableDepthTest={disableDepthTest}
        />
      )
    }
    case 'screw':
      return (
        <CircleGhost
          diameter={preview.diameter}
          transform={transform}
          color={color}
          disableDepthTest={disableDepthTest}
        />
      )
  }
}

/** Map a Redux side-edge feature to the PreviewGhost `preview` shape. */
const featureToGhostPreview = (edge: SideEdge, feature: SideEdgeFeatureConfig): PreviewGhostProps['preview'] => ({
  type: feature.type,
  edge,
  position: feature.position,
  width: 'width' in feature ? (feature as {width: number}).width : 0,
  height: 'height' in feature ? (feature as {height: number}).height : 0,
  cornerRadius: 'cornerRadius' in feature ? (feature as {cornerRadius: number}).cornerRadius : 0,
  diameter: 'diameter' in feature ? (feature as {diameter: number}).diameter : 0,
  depth: feature.type === 'sideButton' ? (feature.depth ?? 0.5) : 0,
  chamfer: feature.type === 'sideButton' ? (feature.chamfer ?? 0) : 0,
  pinHoleOffset: feature.type === 'simCardSlot' ? feature.pinHoleOffset : 0,
})

interface SideEdgeGhostModelProps {
  updating: boolean
  /** Set of pending feature keys — accumulates new features, cleared when worker finishes */
  pendingKeys: Set<string>
}

const SideEdgeGhostModel = ({updating, pendingKeys}: SideEdgeGhostModelProps) => {
  const previewMap = useSyncExternalStore(subscribePreviewSideEdge, getPreviewSideEdgeSnapshot)
  const removedMap = useSyncExternalStore(subscribeRemovedSideEdge, getRemovedSideEdgeSnapshot)

  const sideEdgeFeatures = useAppSelector((s) => selectActiveScene(s).sideEdgeFeatures)
  const deviceWidth = useAppSelector((s) => selectActiveScene(s).deviceWidth)
  const deviceHeight = useAppSelector((s) => selectActiveScene(s).deviceHeight)
  const deviceDepth = useAppSelector((s) => selectActiveScene(s).deviceDepth)

  // Clear the preview and removed stores when the worker finishes (updating goes true → false)
  const prevUpdatingRef = useRef(updating)
  useEffect(() => {
    if (prevUpdatingRef.current && !updating) {
      if (previewMap) clearPreviewSideEdge()
      if (removedMap) clearRemovedSideEdge()
    }
    prevUpdatingRef.current = updating
  }, [updating, previewMap, removedMap])

  // Flatten all features
  const allFeatures = useMemo(() => {
    const result: {edge: SideEdge; feature: SideEdgeFeatureConfig; key: string}[] = []
    for (const edge of ['left', 'right', 'top', 'bottom'] as SideEdge[]) {
      for (const feature of sideEdgeFeatures[edge]) {
        result.push({edge, feature, key: `${edge}:${feature.id}`})
      }
    }
    return result
  }, [sideEdgeFeatures])

  const removedEntries = removedMap ? Object.entries(removedMap) : []
  const previewEntries = previewMap ? Object.entries(previewMap) : []

  // Pending = features ADDED or CHANGED since the last worker build (slider commit,
  // undo/redo, feature creation). `pendingKeys` is computed in DeviceMeshV2 by diffing
  // the side-edge features and is cleared when the worker finishes — so only the
  // feature(s) the edit actually touched get a ghost, never the whole set. Keys already
  // shown live from the preview store (active drag) are skipped to avoid a double ghost.
  const pendingFeatures =
    pendingKeys.size > 0
      ? allFeatures.filter((f) => pendingKeys.has(f.key) && !(previewMap && f.key in previewMap))
      : []

  if (previewEntries.length === 0 && pendingFeatures.length === 0 && removedEntries.length === 0) return null

  return (
    <group>
      {/* Ghosts from preview store (active slider drag) */}
      {previewEntries.map(([key, p]) => (
        <PreviewGhost
          key={key}
          previewKey={key}
          preview={p}
          deviceWidth={deviceWidth}
          deviceHeight={deviceHeight}
          deviceDepth={deviceDepth}
        />
      ))}
      {/* Ghosts for pending (added/changed) features not covered by an active drag preview */}
      {pendingFeatures.map(({edge, feature, key}) => (
        <PreviewGhost
          key={key}
          previewKey={key}
          preview={featureToGhostPreview(edge, feature)}
          deviceWidth={deviceWidth}
          deviceHeight={deviceHeight}
          deviceDepth={deviceDepth}
        />
      ))}
      {/* Red ghosts for removed features — persist until the worker finishes */}
      {removedEntries.map(([key, r]) => (
        <PreviewGhost
          key={`removed-${key}`}
          previewKey={key}
          preview={{...r, diameter: 'diameter' in r ? (r as {diameter: number}).diameter : 0}}
          deviceWidth={deviceWidth}
          deviceHeight={deviceHeight}
          deviceDepth={deviceDepth}
          color={REMOVED_COLOR}
          disableDepthTest
        />
      ))}
    </group>
  )
}

export default SideEdgeGhostModel
